import type { Lang, LexiconSet, RoundState, SubmitInput, SubmitOutcome } from '@polyglot/engine';
import { stopRound, submitWord, finalizeIfExpired } from '@polyglot/engine';

/**
 * A round driver hides who is authoritative: the local engine (solo/robots, never ranked) or
 * the server (online games). The UI only talks to this interface.
 */
export interface RoundDriver {
  readonly kind: 'local' | 'online';
  getState(): RoundState;
  /** Current time on the authority clock (ms). */
  now(): number;
  submit(input: SubmitInput): Promise<SubmitOutcome>;
  stop(): Promise<void>;
  subscribe(cb: () => void): () => void;
  /** Lexicons usable for local feedback (hints are only shown when allowed). */
  lexicons: LexiconSet;
  hintsAllowed: boolean;
}

export class LocalDriver implements RoundDriver {
  readonly kind = 'local' as const;
  private state: RoundState;
  private listeners = new Set<() => void>();
  private timer: number | null = null;

  readonly lexicons: LexiconSet;
  readonly hintsAllowed: boolean;
  private readonly persist: ((s: RoundState) => void) | undefined;

  constructor(
    state: RoundState,
    lexicons: LexiconSet,
    hintsAllowed: boolean,
    persist?: (s: RoundState) => void,
  ) {
    this.state = state;
    this.lexicons = lexicons;
    this.hintsAllowed = hintsAllowed;
    this.persist = persist;
    this.timer = window.setInterval(() => this.tick(), 250);
  }

  now(): number {
    return Date.now();
  }

  getState(): RoundState {
    return this.state;
  }

  private set(s: RoundState): void {
    if (s === this.state) return;
    this.state = s;
    this.persist?.(s);
    for (const l of this.listeners) l();
    if (s.status === 'finished') this.dispose();
  }

  private tick(): void {
    this.set(finalizeIfExpired(this.state, this.now()));
  }

  async submit(input: SubmitInput): Promise<SubmitOutcome> {
    const r = submitWord(this.state, input, this.now(), this.lexicons);
    this.set(r.state);
    return r.outcome;
  }

  async stop(): Promise<void> {
    this.set(stopRound(this.state, this.now()));
  }

  subscribe(cb: () => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  dispose(): void {
    if (this.timer !== null) window.clearInterval(this.timer);
    this.timer = null;
  }
}

export function languagesOf(state: RoundState): Lang[] {
  return state.config.languages;
}
